import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { 
  User, 
  CreditCard, 
  Image as ImageIcon, 
  Plus, 
  LogOut,
  Activity,
  Zap
} from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import { useCredits } from '../context/CreditContext'
import { GeneratedImage } from '../types'
import { imageService } from '../services/images'
import LoadingSpinner from '../components/LoadingSpinner'

export default function DashboardPage() {
  const { user, logout, refreshUser } = useAuth()
  const { transactions } = useCredits()
  const [recentImages, setRecentImages] = useState<GeneratedImage[]>([])
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    loadRecentImages()
    // Refresh user data when dashboard loads to ensure latest credits
    refreshUser()
  }, [])

  const loadRecentImages = async () => {
    try {
      const response = await imageService.getUserImages()
      if (response.success && response.data) {
        setRecentImages(response.data.slice(0, 6)) // Show last 6 images
      }
    } catch (error) {
      console.error('Failed to load images:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleLogout = () => {
    logout()
    navigate('/')
  }

  if (!user) {
    return null
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <Link to="/" className="text-xl font-bold text-gray-900">
                Sku Studio
              </Link>
              <div className="flex items-center space-x-2 text-sm text-gray-500">
                <User className="w-4 h-4" />
                <span>{user.email}</span>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2 bg-blue-50 px-3 py-1 rounded-full">
                <Zap className="w-4 h-4 text-blue-600" />
                <span className="text-blue-600 font-medium">{user.credits} 积分</span>
              </div>
              
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">退出</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            欢迎回来！
          </h1>
          <p className="text-gray-600">
            开始使用AI生成专业的产品图片
          </p>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Link
            to="/generate"
            className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow group"
          >
            <div className="flex items-center space-x-4">
              <div className="bg-blue-100 p-3 rounded-full group-hover:bg-blue-200 transition-colors">
                <Plus className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">生成图片</h3>
                <p className="text-sm text-gray-600">使用AI创建产品图片</p>
              </div>
            </div>
          </Link>

          <Link
            to="/credits"
            className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow group"
          >
            <div className="flex items-center space-x-4">
              <div className="bg-green-100 p-3 rounded-full group-hover:bg-green-200 transition-colors">
                <CreditCard className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">管理积分</h3>
                <p className="text-sm text-gray-600">购买积分或查看使用记录</p>
              </div>
            </div>
          </Link>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center space-x-4">
              <div className="bg-purple-100 p-3 rounded-full">
                <Activity className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">使用统计</h3>
                <p className="text-sm text-gray-600">
                  已生成 {recentImages.length} 张图片
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Recent Images */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                  <ImageIcon className="w-5 h-5" />
                  <span>最近生成的图片</span>
                </h2>
                <Link
                  to="/generate"
                  className="text-sm text-blue-600 hover:text-blue-700"
                >
                  生成更多
                </Link>
              </div>
            </div>

            <div className="p-6">
              {loading ? (
                <div className="flex justify-center py-8">
                  <LoadingSpinner size="md" />
                </div>
              ) : recentImages.length === 0 ? (
                <div className="text-center py-8">
                  <ImageIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">还没有生成任何图片</p>
                  <Link
                    to="/generate"
                    className="inline-flex items-center space-x-2 text-blue-600 hover:text-blue-700 mt-2"
                  >
                    <Plus className="w-4 h-4" />
                    <span>立即开始</span>
                  </Link>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {recentImages.map((image) => (
                    <div
                      key={image.id}
                      className="aspect-square bg-gray-100 rounded-lg overflow-hidden hover:shadow-md transition-shadow"
                    >
                      <img
                        src={image.generatedImageUrl}
                        alt="Generated product"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Recent Transactions */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                  <Activity className="w-5 h-5" />
                  <span>最近交易</span>
                </h2>
                <Link
                  to="/credits"
                  className="text-sm text-blue-600 hover:text-blue-700"
                >
                  查看全部
                </Link>
              </div>
            </div>

            <div className="p-6">
              {transactions.length === 0 ? (
                <div className="text-center py-8">
                  <Activity className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">暂无交易记录</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {transactions.slice(0, 5).map((transaction) => (
                    <div
                      key={transaction.id}
                      className="flex justify-between items-center py-2"
                    >
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {transaction.description}
                        </p>
                        <p className="text-xs text-gray-500">
                          {new Date(transaction.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                      <div className={`text-sm font-medium ${
                        transaction.amount > 0 
                          ? 'text-green-600' 
                          : 'text-red-600'
                      }`}>
                        {transaction.amount > 0 ? '+' : ''}{transaction.amount}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Low Credit Warning */}
        {user.credits < 1 && (
          <div className="mt-8 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center space-x-3">
              <Zap className="w-5 h-5 text-yellow-600" />
              <div className="flex-1">
                <h3 className="text-sm font-medium text-yellow-800">
                  积分不足
                </h3>
                <p className="text-sm text-yellow-700">
                  您的积分已用完，请购买更多积分以继续生成图片。
                </p>
              </div>
              <Link
                to="/credits"
                className="btn-primary text-sm"
              >
                购买积分
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}